interface MonthlyStat {
  month: string;
  sent: number;
  success: number;
  fail: number;
}

interface Props {
  monthlyStats: MonthlyStat[];
  loading: boolean;
}

export default function AgencyChart({ monthlyStats, loading }: Props) {
  const maxVal = Math.max(...monthlyStats.map((s) => s.sent), 1);

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-white font-semibold text-base">월별 발송 추이</h3>
          <p className="text-gray-500 text-xs mt-0.5">최근 6개월 하위 회원 발송 현황</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-amber-500"></span>
            <span className="text-gray-500 text-xs">발송</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500"></span>
            <span className="text-gray-500 text-xs">성공</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-red-500"></span>
            <span className="text-gray-500 text-xs">실패</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-amber-500/40 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      ) : monthlyStats.length === 0 ? (
        <div className="h-48 flex flex-col items-center justify-center">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/[0.04] mb-2">
            <i className="ri-bar-chart-grouped-line text-gray-600 text-xl"></i>
          </div>
          <p className="text-gray-500 text-sm">발송 데이터가 없습니다</p>
        </div>
      ) : (
        <div className="h-48 flex items-end gap-4 px-2">
          {monthlyStats.map((stat, idx) => {
            const sentHeight = (stat.sent / maxVal) * 100;
            const successHeight = stat.sent > 0 ? (stat.success / stat.sent) * sentHeight : 0;
            const failHeight = stat.sent > 0 ? (stat.fail / stat.sent) * sentHeight : 0;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center gap-2 group">
                <div className="w-full flex flex-col items-center relative">
                  {/* Tooltip */}
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#1a1a2e] border border-white/[0.12] rounded-lg px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
                    <p className="text-white text-xs font-medium">{stat.sent.toLocaleString()}건 발송</p>
                    <p className="text-emerald-400 text-[10px]">성공 {stat.success.toLocaleString()}건</p>
                    <p className="text-red-400 text-[10px]">실패 {stat.fail.toLocaleString()}건</p>
                  </div>
                  {/* Bar container */}
                  <div className="w-full max-w-14 flex flex-col justify-end rounded-t-lg overflow-hidden" style={{ height: `${Math.max(sentHeight, 4)}%` }}>
                    {/* Success portion */}
                    <div className="w-full bg-emerald-500/80" style={{ height: `${stat.sent > 0 ? (stat.success / stat.sent) * 100 : 0}%`, minHeight: stat.success > 0 ? 2 : 0 }}></div>
                    {/* Fail portion */}
                    <div className="w-full bg-red-500/80" style={{ height: `${stat.sent > 0 ? (stat.fail / stat.sent) * 100 : 0}%`, minHeight: stat.fail > 0 ? 2 : 0 }}></div>
                    {/* Remaining (sent portion background) */}
                    <div className="w-full bg-amber-500/40" style={{ height: `${stat.sent > 0 ? ((stat.sent - stat.success - stat.fail) / stat.sent) * 100 : 0}%`, minHeight: 0 }}></div>
                  </div>
                </div>
                <span className="text-gray-500 text-xs">{stat.month}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}